import React, { useState, useEffect, useCallback, useRef } from 'react';
import fprService from '../services/fprService';
import FprFormView from './FprFormView';
import FprSharePointModal from './FprSharePointModal';
import { exportFprFormToPdf } from './fprPdfExport';
import { autoFillAdvisor } from './fprAdvisor';
import ClientFormScheduleModal from './ClientFormScheduleModal';

const TAMARAC_CACHE_TTL_MS = 5 * 60 * 1000;
const TAMARAC_LAST_QUERY_KEY = 'fpr_tamarac_last_query';
const tamaracSearchCache = new Map();

function normalizeTamaracQuery(query) {
  return (query || '').trim().toLowerCase();
}

function getCachedTamaracOptions(query) {
  const key = normalizeTamaracQuery(query);
  if (key.length < 2) return null;

  const hit = tamaracSearchCache.get(key);
  if (!hit) return null;
  if ((Date.now() - hit.timestamp) > TAMARAC_CACHE_TTL_MS) {
    tamaracSearchCache.delete(key);
    return null;
  }
  return hit.options;
}

function setCachedTamaracOptions(query, options) {
  const key = normalizeTamaracQuery(query);
  if (key.length < 2) return;
  tamaracSearchCache.set(key, {
    timestamp: Date.now(),
    options: Array.isArray(options) ? options : [],
  });
}

function createEmptyFormData() {
  return {
    tamarac_contact_id: '',
    tamarac_household_id: '',
    household_name: '',
    birth_date: '',
    middle_name: '',
    portal_url: '',
    portal_status: '',
    preferred_delivery_method: '',
    portal_enabled: null,
    is_ucp_enabled: null,
    client_id: '',
    client_crm_id: '',
    default_account: '',
    custom_account: '',
    tamarac_raw_keys: [],
    prefill: null,
    name: '',
    email: '',
    phone: '',
    notes: '',
    secondary_advisor: '',
    client_type: 'hff',
    send_mode: 'now',
    scheduled_send_local: '',
  };
}

function resolveSecondaryAdvisorKey(advisorName) {
  const normalized = String(advisorName || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'ben' || normalized.includes('ben')) return 'ben';
  if (normalized === 'tiffany' || normalized.includes('tiffany')) return 'tiffany';
  if (normalized === 'lisa' || normalized.includes('lisa')) return 'lisa';
  return '';
}

function mergeSecondaryContactPrefill(prefill, details) {
  const nextPrefill = { ...(prefill && typeof prefill === 'object' ? prefill : {}) };

  const secondaryFirstName = String(details?.secondaryFirstName || '').trim();
  const secondaryLastName = String(details?.secondaryLastName || '').trim();
  const secondaryEmail = String(details?.secondaryEmail || '').trim();
  const secondaryAdvisorName = String(details?.secondaryAdvisorName || '').trim();
  const rebellionaire = !!details?.Rebellionaire;

  nextPrefill.secondary_advisor_name = secondaryAdvisorName;
  nextPrefill.secondary_first_name = secondaryFirstName;
  nextPrefill.secondary_last_name = secondaryLastName;
  nextPrefill.secondary_email = secondaryEmail;
  nextPrefill.rebellionaire = rebellionaire;

  if (secondaryFirstName || secondaryLastName || secondaryEmail) {
    nextPrefill.has_partner = 'yes';
    nextPrefill.partner_first_name = secondaryFirstName;
    nextPrefill.partner_last_name = secondaryLastName;
    nextPrefill.partner_email = secondaryEmail;
  }

  return nextPrefill;
}

function getMeetingState(client) {
  const rawStatus = String(client?.meeting_status || '').trim().toLowerCase();
  if (rawStatus === 'canceled' || rawStatus === 'cancelled') {
    return 'canceled';
  }
  if (rawStatus === 'scheduled' || client?.meeting_scheduled) {
    return 'scheduled';
  }
  return 'not_scheduled';
}

/**
 * FprAdmin — advisor-facing client management for the FPR Update Form.
 *
 * Renders inside the unified ClientForms page as one of the form-type
 * tabs. Mirrors the cashflow admin layout (search, filter, table,
 * detail panel) but uses the FPR backend service.
 */
const FprAdmin = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(createEmptyFormData);
  const [tamaracQuery, setTamaracQuery] = useState('');
  const [tamaracOptions, setTamaracOptions] = useState([]);
  const [tamaracLoading, setTamaracLoading] = useState(false);
  const [showTamaracOptions, setShowTamaracOptions] = useState(false);
  const tamaracRequestSeqRef = useRef(0);
  const tamaracJustPickedRef = useRef(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [linkClient, setLinkClient] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleClient, setScheduleClient] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showProcessed, setShowProcessed] = useState(false);
  const [meetingFilter, setMeetingFilter] = useState('all');
  const [advisorFilter, setAdvisorFilter] = useState('all');

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [detailClient, setDetailClient] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  // Ignore stale Tamarac details / client-detail responses when selection changes.
  const tamaracDetailsRequestSeqRef = useRef(0);
  const tamaracDetailsAbortRef = useRef(null);
  const detailRequestSeqRef = useRef(0);
  const detailAbortRef = useRef(null);

  // Abort any in-flight requests when leaving the page to avoid late setState calls.
  useEffect(() => {
    return () => {
      tamaracDetailsRequestSeqRef.current += 1;
      detailRequestSeqRef.current += 1;

      if (tamaracDetailsAbortRef.current) {
        tamaracDetailsAbortRef.current.abort();
        tamaracDetailsAbortRef.current = null;
      }
      if (detailAbortRef.current) {
        detailAbortRef.current.abort();
        detailAbortRef.current = null;
      }
    };
  }, []);

  // Publish to SharePoint modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishClient, setPublishClient] = useState(null);

  // Per-row download spinner (client_id currently being exported).
  const [exportingId, setExportingId] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fprService.fetchAllClients();
      setClients(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Search via Power Automate account-search flow, debounced 400 ms.
  useEffect(() => {
    // If the query change came from picking a result (not user typing), skip search.
    if (tamaracJustPickedRef.current) {
      tamaracJustPickedRef.current = false;
      return undefined;
    }

    const query = tamaracQuery.trim();
    if (!showCreateModal) return undefined;

    if (query.length < 2) {
      setTamaracOptions([]);
      setShowTamaracOptions(false);
      return undefined;
    }

    const cached = getCachedTamaracOptions(query);
    if (cached) {
      setTamaracOptions(cached);
      setShowTamaracOptions(true);
      return undefined;
    }

    let active = true;
    const requestId = ++tamaracRequestSeqRef.current;

    const timer = setTimeout(() => {
      setTamaracLoading(true);
      setShowTamaracOptions(true);

      fprService
        .searchTamaracClientsPA(query, 25)
        .then((results) => {
          if (!active || requestId !== tamaracRequestSeqRef.current) return;
          const safe = Array.isArray(results) ? results : [];
          setCachedTamaracOptions(query, safe);
          setTamaracOptions(safe);
        })
        .catch(() => {
          if (!active || requestId !== tamaracRequestSeqRef.current) return;
          setTamaracOptions([]);
        })
        .finally(() => {
          if (active && requestId === tamaracRequestSeqRef.current) {
            setTamaracLoading(false);
          }
        });
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [tamaracQuery, showCreateModal]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function sortIcon(key) {
    if (sortKey !== key) {
      return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }} />;
    }
    return sortDir === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }} />
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }} />;
  }

  const filteredClients = clients.filter((c) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term);
    if (!showProcessed && c.processed) return false;

    const filled = c.sections_completed || 0;
    const isComplete = c.completed || (c.total_sections > 0 && filled === c.total_sections);
    const status = isComplete ? 'completed' : filled > 0 ? 'in_progress' : 'not_started';
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    const meetingState = getMeetingState(c);
    const matchesMeeting = meetingFilter === 'all' || meetingState === meetingFilter;

    const advisorKey = (c.secondary_advisor || '').toLowerCase();
    const matchesAdvisor = advisorFilter === 'all' || advisorKey === advisorFilter;

    return matchesSearch && matchesStatus && matchesMeeting && matchesAdvisor;
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (!sortKey) return 0;
    let av, bv;
    switch (sortKey) {
      case 'name':    av = a.name || '';  bv = b.name || ''; break;
      case 'email':   av = a.email || ''; bv = b.email || ''; break;
      case 'status':  av = (a.completed || (a.total_sections > 0 && a.sections_completed === a.total_sections)) ? 1 : (a.sections_completed || 0) > 0 ? 0.5 : 0;
                       bv = (b.completed || (b.total_sections > 0 && b.sections_completed === b.total_sections)) ? 1 : (b.sections_completed || 0) > 0 ? 0.5 : 0;
                       av = a.processed ? 2 : av;
                       bv = b.processed ? 2 : bv; break;
      case 'advisor': av = (a.secondary_advisor_label || a.secondary_advisor || '').toLowerCase();
                       bv = (b.secondary_advisor_label || b.secondary_advisor || '').toLowerCase(); break;
      case 'meeting': av = `${getMeetingState(a)}:${a.meeting_scheduled_at || ''}`;
               bv = `${getMeetingState(b)}:${b.meeting_scheduled_at || ''}`; break;
      case 'created': av = a.created_at || ''; bv = b.created_at || ''; break;
      default: return 0;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.secondary_advisor) {
      setError('Please select the secondary advisor (Ben, Tiffany or Lisa).');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        notes: formData.notes || null,
        knowledgebase_client_id: formData.tamarac_contact_id,
        prefill: {
          ...(formData.prefill || {}),
          rebellionaire: formData.client_type === 'rebellionaire',
        },
        secondary_advisor: formData.secondary_advisor,
        client_type: formData.client_type || 'hff',
        send_mode: formData.send_mode || 'now',
        scheduled_send_at: formData.scheduled_send_local
          ? new Date(formData.scheduled_send_local).toISOString()
          : null,
      };
      const result = await fprService.createClient(payload);
      if (result.delivery_status === 'scheduled') {
        setSuccess(`Client created. Initial send scheduled for ${new Date(result.scheduled_send_at).toLocaleString()}.`);
      } else {
        setSuccess(`Client created. Magic link: ${result.magic_link}`);
      }
      setShowCreateModal(false);
      setFormData(createEmptyFormData());
      setTamaracQuery('');
      setTamaracOptions([]);
      setShowTamaracOptions(false);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePickTamaracClient = (contactId) => {
    const picked = tamaracOptions.find((opt) => opt.tamarac_contact_id === contactId);
    if (!picked) {
      if (tamaracDetailsAbortRef.current) {
        tamaracDetailsAbortRef.current.abort();
        tamaracDetailsAbortRef.current = null;
      }
      tamaracDetailsRequestSeqRef.current += 1;
      setDetailsLoading(false);
      setFormData(createEmptyFormData());
      return;
    }

    // Cancel any in-flight details fetch from a previous pick so a slower
    // response cannot overwrite the currently selected client's prefill.
    if (tamaracDetailsAbortRef.current) {
      tamaracDetailsAbortRef.current.abort();
      tamaracDetailsAbortRef.current = null;
    }
    const requestSeq = tamaracDetailsRequestSeqRef.current + 1;
    tamaracDetailsRequestSeqRef.current = requestSeq;
    const selectedContactId = picked.tamarac_contact_id;

    // Immediately populate from picker data so the form is usable right away.
    setFormData((prev) => ({
      ...prev,
      tamarac_contact_id: picked.tamarac_contact_id,
      tamarac_household_id: picked.tamarac_household_id || '',
      household_name: picked.household_name || '',
      birth_date: picked.birth_date || '',
      middle_name: picked.middle_name || '',
      portal_url: picked.portal_url || '',
      portal_status: picked.portal_status || '',
      preferred_delivery_method: picked.preferred_delivery_method || '',
      portal_enabled: typeof picked.portal_enabled === 'boolean' ? picked.portal_enabled : null,
      is_ucp_enabled: typeof picked.is_ucp_enabled === 'boolean' ? picked.is_ucp_enabled : null,
      client_id: picked.client_id || '',
      client_crm_id: picked.client_crm_id || '',
      default_account: picked.default_account || '',
      custom_account: picked.custom_account || '',
      tamarac_raw_keys: Array.isArray(picked.tamarac_raw_keys) ? picked.tamarac_raw_keys : [],
      prefill: picked.prefill || null,
      name: picked.tamarac_display_name || '',
      email: picked.email || '',
      phone: picked.phone || '',
      secondary_advisor: '',
      client_type: picked.prefill?.rebellionaire ? 'rebellionaire' : 'hff',
    }));
    tamaracJustPickedRef.current = true;
    setTamaracQuery(
      `${picked.tamarac_display_name || 'Unknown Client'}${picked.email ? ` (${picked.email})` : ''}`,
    );
    if (picked.tamarac_display_name || picked.email) {
      localStorage.setItem(
        TAMARAC_LAST_QUERY_KEY,
        String(picked.tamarac_display_name || picked.email || '').trim(),
      );
    }
    setShowTamaracOptions(false);

    // PA search results already embed secondary-advisor data in the prefill;
    // apply it directly without a follow-up network call.
    if (picked.source === 'pa') {
      const paPrefill = picked.prefill || {};
      const resolvedAdvisor = resolveSecondaryAdvisorKey(paPrefill.secondary_advisor_name);
      const mergedPrefill = mergeSecondaryContactPrefill(null, {
        secondaryAdvisorName: paPrefill.secondary_advisor_name || '',
        secondaryFirstName: paPrefill.secondary_first_name || '',
        secondaryLastName: paPrefill.secondary_last_name || '',
        secondaryEmail: paPrefill.secondary_email || '',
        Rebellionaire: paPrefill.rebellionaire || false,
      });
      // Merge PA-sourced prefill fields (name, email) into the final prefill.
      setFormData((prev) => {
        if (prev.tamarac_contact_id !== selectedContactId) {
          return prev;
        }
        return {
          ...prev,
          prefill: { ...paPrefill, ...mergedPrefill },
          secondary_advisor: autoFillAdvisor(prev.secondary_advisor, resolvedAdvisor),
        };
      });
      setDetailsLoading(false);
      return;
    }

    // Non-PA results: fetch contact details in the background.
    const detailsLookupId = picked.client_crm_id || picked.tamarac_contact_id;
    if (detailsLookupId) {
      const controller = new AbortController();
      tamaracDetailsAbortRef.current = controller;
      setDetailsLoading(true);
      fprService.fetchTamaracContactDetails(detailsLookupId, { signal: controller.signal })
        .then((details) => {
          if (requestSeq !== tamaracDetailsRequestSeqRef.current) {
            return;
          }
          const resolvedSecondaryAdvisor = resolveSecondaryAdvisorKey(details.secondaryAdvisorName);
          const mergedPrefill = mergeSecondaryContactPrefill(picked.prefill || null, details);
          setFormData((prev) => {
            // Drop the response if the advisor already picked a different client.
            if (prev.tamarac_contact_id !== selectedContactId) {
              return prev;
            }
            return {
              ...prev,
              prefill: mergedPrefill,
              secondary_advisor: autoFillAdvisor(prev.secondary_advisor, resolvedSecondaryAdvisor),
            };
          });
        })
        .catch((err) => {
          if (
            controller.signal.aborted
            || err?.code === 'ERR_CANCELED'
            || err?.name === 'CanceledError'
            || err?.name === 'AbortError'
          ) {
            return;
          }
          if (requestSeq !== tamaracDetailsRequestSeqRef.current) {
            return;
          }
          setError(err.response?.data?.detail || err.message);
        })
        .finally(() => {
          if (tamaracDetailsAbortRef.current === controller) {
            tamaracDetailsAbortRef.current = null;
          }
          if (requestSeq === tamaracDetailsRequestSeqRef.current) {
            setDetailsLoading(false);
          }
        });
    } else {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      setSubmitting(true);
      setError('');
      await fprService.deleteClient(selectedClient.client_id);
      setSuccess(`Deleted client: ${selectedClient.name}`);
      setShowDeleteModal(false);
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (client) => {
    try {
      setError('');
      const result = await fprService.resendLink(client.client_id);
      setSuccess(`Magic link resent to ${client.name}: ${result.magic_link}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleTogglePause = async (client) => {
    try {
      setError('');
      const pausing = !client.reminders_paused;
      await fprService.patchClient(client.client_id, { reminders_paused: pausing });
      setSuccess(`Reminders ${pausing ? 'paused' : 'resumed'} for ${client.name}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleToggleProcessed = async (client) => {
    try {
      setError('');
      const nextProcessed = !client.processed;
      await fprService.patchClient(client.client_id, { processed: nextProcessed });
      setSuccess(`${nextProcessed ? 'Marked' : 'Unmarked'} as processed: ${client.name}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleExport = async (client) => {
    try {
      setError('');
      setExportingId(client.client_id);
      const data = await fprService.getClientDetail(client.client_id);
      const formData = data?.form?.form_data || {};
      exportFprFormToPdf(formData, {
        clientName: client.name,
        clientEmail: client.email,
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'PDF export failed');
    } finally {
      setExportingId(null);
    }
  };

  const handlePublish = (client) => {
    setPublishClient(client);
    setShowPublishModal(true);
  };

  const handlePublishSuccess = (result) => {
    setShowPublishModal(false);
    setPublishClient(null);
    setSuccess(
      `Published to SharePoint: ${result.sharepoint_location || 'success'}`
      + (result.knowledgebase_status === 'added_to_knowledgebase'
        ? ' — added to knowledgebase.'
        : ''),
    );
  };

  const handleGetLink = async (client) => {
    setLinkClient(client);
    setMagicLink('');
    setShowLinkModal(true);
    try {
      setLinkLoading(true);
      setError('');
      const result = await fprService.getLink(client.client_id);
      setMagicLink(result.magic_link);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setShowLinkModal(false);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleOpenScheduleModal = (client) => {
    setScheduleClient(client);
    setShowScheduleModal(true);
  };

  const handleScheduleSave = async (payload) => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await fprService.scheduleSend(scheduleClient.client_id, payload);
      setSuccess(`Scheduled send saved for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleCancel = async () => {
    if (!scheduleClient) return;
    try {
      setSubmitting(true);
      setError('');
      await fprService.cancelScheduledSend(scheduleClient.client_id);
      setSuccess(`Canceled scheduled send for ${scheduleClient.name}.`);
      setShowScheduleModal(false);
      setScheduleClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewClient = async (client) => {
    if (detailClient?.client_id === client.client_id) {
      if (detailAbortRef.current) {
        detailAbortRef.current.abort();
        detailAbortRef.current = null;
      }
      detailRequestSeqRef.current += 1;
      setDetailClient(null);
      setDetailForm(null);
      setDetailLoading(false);
      return;
    }

    // Abort any in-flight detail fetch so a slower prior response cannot
    // paint one client's form under another client's name header.
    if (detailAbortRef.current) {
      detailAbortRef.current.abort();
      detailAbortRef.current = null;
    }
    const requestSeq = detailRequestSeqRef.current + 1;
    detailRequestSeqRef.current = requestSeq;
    const controller = new AbortController();
    detailAbortRef.current = controller;
    const requestedClientId = client.client_id;

    try {
      setDetailLoading(true);
      setDetailClient(client);
      setDetailForm(null);
      const data = await fprService.getClientDetail(requestedClientId, {
        signal: controller.signal,
      });
      if (requestSeq !== detailRequestSeqRef.current) {
        return;
      }
      setDetailForm(data.form?.form_data || {});
    } catch (err) {
      if (
        controller.signal.aborted
        || err?.code === 'ERR_CANCELED'
        || err?.name === 'CanceledError'
        || err?.name === 'AbortError'
      ) {
        return;
      }
      if (requestSeq !== detailRequestSeqRef.current) {
        return;
      }
      setError(err.response?.data?.detail || err.message);
      setDetailClient(null);
      setDetailForm(null);
    } finally {
      if (detailAbortRef.current === controller) {
        detailAbortRef.current = null;
      }
      if (requestSeq === detailRequestSeqRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatMeetingTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">
          <i className="bi bi-info-circle me-1" />
          Send the FPR Update Form to clients ahead of their scheduled
          Financial Planning Review.
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-person-check me-1" /> Select Tamarac Client
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div style={{ wordBreak: 'break-all' }}>{success}</div>
          {success.includes('http') && (
            <button
              className="btn btn-sm btn-outline-success mt-2"
              onClick={() => {
                const url = success.match(/(https?:\/\/\S+)/)?.[1];
                if (url) copyToClipboard(url, 'success');
              }}
            >
              <i className="bi bi-clipboard me-1" />
              {copiedId === 'success' ? 'Copied!' : 'Copy Link'}
            </button>
          )}
          <button type="button" className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <div className="mb-3 d-flex gap-2 flex-wrap align-items-center">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="btn-group">
          {[
            { key: 'all',         label: 'All' },
            { key: 'completed',   label: 'Completed' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'not_started', label: 'Not Started' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="btn-group">
          {[
            { key: 'all',           label: 'Any Meeting' },
            { key: 'scheduled',     label: 'Scheduled' },
            { key: 'canceled',      label: 'Canceled' },
            { key: 'not_scheduled', label: 'Not Scheduled' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${meetingFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setMeetingFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="btn-group">
          {[
            { key: 'all',     label: 'Any Advisor' },
            { key: 'ben',     label: 'Ben' },
            { key: 'tiffany', label: 'Tiffany' },
            { key: 'lisa',    label: 'Lisa' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${advisorFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setAdvisorFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`btn btn-sm ${showProcessed ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => setShowProcessed((v) => !v)}
          title={showProcessed ? 'Showing processed workflows' : 'Hiding processed workflows'}
        >
          <i className={`bi ${showProcessed ? 'bi-eye' : 'bi-eye-slash'} me-1`} />
          {showProcessed ? 'Hide Processed' : 'Show Processed'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox display-4 d-block mb-2" />
          {searchTerm || statusFilter !== 'all' || meetingFilter !== 'all' || advisorFilter !== 'all' || !showProcessed
            ? 'No clients match your filters.'
            : 'No FPR clients yet. Click "Select Tamarac Client" to create one.'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{sortIcon('email')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('advisor')}>Advisor{sortIcon('advisor')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('meeting')}>Meeting{sortIcon('meeting')}</th>
                <th>Reminders</th>
                <th>Next Send</th>
                <th>Device</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('created')}>Created{sortIcon('created')}</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.map((client) => {
                const filled = client.sections_completed || 0;
                const totalSections = client.total_sections || 9;
                const pct = (client.completed || filled === totalSections) ? 100 : Math.round((filled / totalSections) * 100);
                const color = (client.completed || filled === totalSections) ? 'bg-success' : pct > 0 ? 'bg-info' : 'bg-secondary';
                const meetingState = getMeetingState(client);
                return (
                  <tr
                    key={client.client_id}
                    onClick={() => handleViewClient(client)}
                    style={{ cursor: 'pointer' }}
                    className={detailClient?.client_id === client.client_id ? 'table-active' : ''}
                  >
                    <td className="fw-semibold">{client.name}</td>
                    <td>{client.email}</td>
                    <td style={{ minWidth: '140px' }}>
                      <div className="progress" style={{ height: '18px' }}>
                        <div
                          className={`progress-bar ${color}`}
                          role="progressbar"
                          style={{ width: `${Math.max(pct, pct > 0 ? 10 : 0)}%` }}
                        >
                          {pct > 0 ? `${pct}%` : ''}
                        </div>
                      </div>
                      <small className="text-muted">
                        {client.processed
                          ? 'Processed'
                          : ((client.completed || filled === totalSections) ? 'Completed' : pct === 0 ? 'Not Started' : `${filled} of ${totalSections} sections`)}
                      </small>
                    </td>
                    <td>
                      {client.secondary_advisor_label
                        || (client.secondary_advisor
                          ? client.secondary_advisor.charAt(0).toUpperCase() + client.secondary_advisor.slice(1)
                          : <span className="text-muted">—</span>)}
                    </td>
                    <td>
                      {meetingState === 'scheduled' ? (
                        <span className="text-success">
                          <i className="bi bi-calendar2-check me-1" />
                          {client.meeting_scheduled_at
                            ? formatMeetingTime(client.meeting_scheduled_at)
                            : 'Scheduled'}
                        </span>
                      ) : meetingState === 'canceled' ? (
                        <span className="text-warning">
                          <i className="bi bi-calendar2-x me-1" />
                          {client.meeting_scheduled_at
                            ? `Canceled: ${formatMeetingTime(client.meeting_scheduled_at)}`
                            : 'Canceled'}
                        </span>
                      ) : (
                        <span className="text-muted">
                          <i className="bi bi-calendar2-x me-1" />
                          Not scheduled
                        </span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        if (client.processed) {
                          return <span className="text-muted">—</span>;
                        }
                        if (client.completed || (client.total_sections > 0 && (client.sections_completed || 0) === client.total_sections)) {
                          return <span className="text-muted">—</span>;
                        }
                        if (client.reminders_paused) {
                          return (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-pause-circle me-1" />Paused
                            </span>
                          );
                        }
                        const ref = client.last_reminder_sent || client.created_at;
                        if (!ref) return <span className="text-muted">—</span>;
                        const daysSince = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
                        const interval = 10;
                        const daysLeft = interval - daysSince;
                        if (daysLeft <= 0) {
                          return (
                            <span className="badge bg-danger">
                              <i className="bi bi-envelope me-1" />Due now
                            </span>
                          );
                        }
                        return (
                          <span className="text-muted small">
                            {daysLeft}d until next
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {client.next_scheduled_send_at ? (
                        <div>
                          <div className="small fw-semibold">{formatDate(client.next_scheduled_send_at)}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {client.next_scheduled_send_reason || 'scheduled'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {client.device_type ? (
                        <span title={client.device_type}>
                          <i className={`bi ${client.device_type === 'mobile' ? 'bi-phone' : 'bi-laptop'}`} />
                          {' '}{client.device_type === 'mobile' ? 'Mobile' : 'Desktop'}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{formatDate(client.created_at)}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-outline-secondary"
                          title="Get magic link"
                          onClick={() => handleGetLink(client)}
                        >
                          <i className="bi bi-link-45deg" />
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Resend magic link via email"
                          onClick={() => handleResend(client)}
                        >
                          <i className="bi bi-send" />
                        </button>
                        <button
                          className={`btn ${client.reminders_paused ? 'btn-warning' : 'btn-outline-secondary'}`}
                          title={client.reminders_paused ? 'Resume reminder emails' : 'Pause reminder emails'}
                          onClick={() => handleTogglePause(client)}
                        >
                          <i className={`bi ${client.reminders_paused ? 'bi-play-circle' : 'bi-pause-circle'} me-1`} />
                          {client.reminders_paused ? 'Resume' : 'Pause'}
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="Schedule next form send"
                          onClick={() => handleOpenScheduleModal(client)}
                        >
                          <i className="bi bi-calendar-plus" />
                        </button>
                        <button
                          className="btn btn-outline-success"
                          title="Download PDF"
                          onClick={() => handleExport(client)}
                          disabled={exportingId === client.client_id}
                        >
                          {exportingId === client.client_id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-file-earmark-pdf" />
                          )}
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Publish to SharePoint"
                          onClick={() => handlePublish(client)}
                        >
                          <i className="bi bi-cloud-upload" />
                        </button>
                        <button
                          className={`btn ${client.processed ? 'btn-warning' : 'btn-outline-warning'}`}
                          title={client.processed ? 'Mark as unprocessed' : 'Mark as processed'}
                          onClick={() => handleToggleProcessed(client)}
                        >
                          <i className={`bi ${client.processed ? 'bi-arrow-counterclockwise' : 'bi-check2-circle'}`} />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete client"
                          onClick={() => { setSelectedClient(client); setShowDeleteModal(true); }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailClient && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-person-lines-fill me-2" />
              {detailClient.name}
              <small className="text-muted ms-2">({detailClient.email})</small>
            </h5>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setDetailClient(null); setDetailForm(null); }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="card-body">
            {detailLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <FprFormView
                formData={detailForm}
                clientName={detailClient.name}
                clientEmail={detailClient.email}
              />
            )}
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create FPR Update Client from Tamarac</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} />
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Search Tamarac Client <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type name or email..."
                        autoComplete="off"
                        value={tamaracQuery}
                        onFocus={() => {
                          if (tamaracOptions.length) setShowTamaracOptions(true);
                        }}
                        onChange={(e) => {
                          setTamaracQuery(e.target.value);
                          setFormData(createEmptyFormData());
                        }}
                      />
                      {tamaracLoading && (
                        <span
                          className="position-absolute top-50 end-0 translate-middle-y me-3"
                          aria-hidden="true"
                          title="Searching…"
                        >
                          <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                        </span>
                      )}
                      {showTamaracOptions && tamaracQuery.trim().length >= 2 && (
                        <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 20, maxHeight: '220px', overflowY: 'auto' }}>
                          {!tamaracLoading && tamaracOptions.length === 0 && (
                            <div className="list-group-item text-muted small">No clients found.</div>
                          )}
                          {tamaracOptions.map((opt) => (
                            <button
                              key={`${opt.tamarac_contact_id}-${opt.email || ''}`}
                              type="button"
                              className="list-group-item list-group-item-action"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handlePickTamaracClient(opt.tamarac_contact_id);
                              }}
                            >
                              <div className="fw-semibold">{opt.tamarac_display_name || 'Unknown Client'}</div>
                              <small className="text-muted">{opt.email || 'No email'}{opt.tamarac_household_id ? ` • Household ${opt.tamarac_household_id}` : ''}</small>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-text">
                      Use at least 2 characters and pick a client from the suggestions.
                    </div>
                  </div>

                  {(formData.tamarac_household_id || formData.household_name) && (
                    <div className="mb-3 form-text">
                      {formData.tamarac_household_id ? `Household ID: ${formData.tamarac_household_id}` : ''}
                      {formData.tamarac_household_id && formData.household_name ? ' • ' : ''}
                      {formData.household_name ? `Household: ${formData.household_name}` : ''}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      readOnly
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Send Timing</label>
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="fpr-send-now"
                          value="now"
                          checked={formData.send_mode === 'now'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="fpr-send-now">Send now</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="send_mode"
                          id="fpr-send-scheduled"
                          value="scheduled"
                          checked={formData.send_mode === 'scheduled'}
                          onChange={(e) => setFormData({ ...formData, send_mode: e.target.value })}
                        />
                        <label className="form-check-label" htmlFor="fpr-send-scheduled">Schedule send</label>
                      </div>
                    </div>
                    {formData.send_mode === 'scheduled' && (
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.scheduled_send_local || ''}
                        onChange={(e) => setFormData({ ...formData, scheduled_send_local: e.target.value })}
                        required
                      />
                    )}
                    <div className="form-text">Choose "Schedule send" to send the magic link later.</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Secondary Advisor <span className="text-danger">*</span>
                      {detailsLoading && (
                        <span className="spinner-border spinner-border-sm ms-2 text-secondary" role="status" aria-label="Loading advisor info" />
                      )}
                    </label>
                    <div className="d-flex gap-3">
                      {[
                        { key: 'ben', label: 'Ben' },
                        { key: 'tiffany', label: 'Tiffany' },
                        { key: 'lisa', label: 'Lisa' },
                      ].map((opt) => (
                        <div className="form-check" key={opt.key}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="secondary_advisor"
                            id={`advisor-${opt.key}`}
                            value={opt.key}
                            checked={formData.secondary_advisor === opt.key}
                            onChange={(e) =>
                              setFormData({ ...formData, secondary_advisor: e.target.value })
                            }
                          />
                          <label className="form-check-label" htmlFor={`advisor-${opt.key}`}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">
                      Determines the scheduling link and email wording sent to the client.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Client Type</label>
                    <div className="d-flex gap-3">
                      {[
                        { key: 'hff', label: 'HFF' },
                        { key: 'rebellionaire', label: 'Rebellionaire' },
                      ].map((opt) => (
                        <div className="form-check" key={String(opt.key)}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="client_type"
                            id={`client-type-${opt.key}`}
                            checked={formData.client_type === opt.key}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                client_type: opt.key,
                              }))
                            }
                          />
                          <label className="form-check-label" htmlFor={`client-type-${opt.key}`}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      submitting
                      || detailsLoading
                      || !tamaracQuery.trim()
                      || (formData.send_mode === 'scheduled' && !formData.scheduled_send_local)
                    }
                  >
                    {submitting ? 'Creating...' : (formData.send_mode === 'scheduled' ? 'Create & Schedule Send' : 'Create & Generate Link')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && linkClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Magic Link — {linkClient.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowLinkModal(false)} />
              </div>
              <div className="modal-body">
                {linkLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Generating link...</span>
                  </div>
                ) : magicLink ? (
                  <div>
                    <p className="text-muted small mb-2">
                      A new magic link has been generated. The previous link is no longer valid.
                    </p>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        readOnly
                        value={magicLink}
                      />
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => copyToClipboard(magicLink, linkClient.client_id)}
                      >
                        <i className="bi bi-clipboard me-1" />
                        {copiedId === linkClient.client_id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowLinkModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete FPR Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete <strong>{selectedClient.name}</strong> ({selectedClient.email})?
                </p>
                <p className="text-muted small">
                  This will permanently remove their access and any saved form data.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
                  {submitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish to SharePoint Modal */}
      {showPublishModal && publishClient && (
        <FprSharePointModal
          client={publishClient}
          onClose={() => { setShowPublishModal(false); setPublishClient(null); }}
          onSuccess={handlePublishSuccess}
        />
      )}

      <ClientFormScheduleModal
        isOpen={showScheduleModal}
        title="Schedule Next FPR Request"
        client={scheduleClient}
        schedule={scheduleClient ? {
          scheduled_send_at: scheduleClient.next_scheduled_send_at,
          send_reason: scheduleClient.next_scheduled_send_reason,
          send_email: scheduleClient.next_scheduled_send_email,
          reset_progress_on_send: true,
        } : null}
        submitting={submitting}
        onClose={() => { setShowScheduleModal(false); setScheduleClient(null); }}
        onSave={handleScheduleSave}
        onCancelSchedule={handleScheduleCancel}
        reasonOptions={[
          { value: 'next_year', label: 'Next Year FPR' },
          { value: 'next_quarter', label: 'Next Quarter FPR' },
          { value: 'custom', label: 'Custom' },
        ]}
        defaultReason="next_year"
      />
    </div>
  );
};

export default FprAdmin;
